import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const LeadForm = ({ lead, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    leadType: 'tour_booking', // Default to tour booking
    serviceType: '', // For other services
    assignedTo: '', // Employee assignment
    customerName: '',
    contactNumber: '',
    email: '',
    tourPackage: '',
    travelDate: '',
    // Hidden fields for backend compatibility
    status: 'New',
    address: '',
    budget: '',
    destination: '',
    numberOfDays: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tourPackages, setTourPackages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);

  // Load tour packages and employees
  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem('companyToken') || localStorage.getItem('employeeToken') || localStorage.getItem('branchToken');

        // Load tour packages
        const packagesRes = await fetch('/tour-packages', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (packagesRes.ok) {
          const packagesData = await packagesRes.json();
          setTourPackages(packagesData.packages || packagesData || []);
        }

        // Load employees
        const employeesRes = await fetch('/employees', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (employeesRes.ok) {
          const employeesData = await employeesRes.json();
          setEmployees(employeesData.data || employeesData || []);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();
  }, []);

  // Filter employees based on service type
  useEffect(() => {
    if (formData.leadType === 'other_services' && formData.serviceType) {
      console.log('🔍 Filtering employees for service:', formData.serviceType);
      console.log('📋 Total employees:', employees.length);
      
      // Log all other_services employees with their specializations
      const otherServicesEmps = employees.filter(emp => emp.teamType === 'other_services');
      console.log('👥 Other Services Employees:', otherServicesEmps.map(emp => ({
        name: emp.teamMemberName,
        specialization: emp.serviceSpecialization
      })));
      
      const filtered = employees.filter(emp => {
        const isOtherServices = emp.teamType === 'other_services';
        const hasSpecialization = emp.serviceSpecialization?.includes(formData.serviceType);
        
        if (isOtherServices) {
          console.log(`  - ${emp.teamMemberName}: specialization=${JSON.stringify(emp.serviceSpecialization)}, matches=${hasSpecialization}`);
        }
        
        return isOtherServices && hasSpecialization;
      });
      
      setFilteredEmployees(filtered);
      console.log('✅ Filtered employees for', formData.serviceType, ':', filtered.length, 'found');
      console.log('📋 Filtered list:', filtered.map(e => e.teamMemberName));
    } else if (formData.leadType === 'tour_booking') {
      // For tour booking, show sales team members
      const filtered = employees.filter(emp => emp.teamType === 'sales');
      setFilteredEmployees(filtered);
    } else {
      setFilteredEmployees([]);
    }
  }, [formData.leadType, formData.serviceType, employees]);

  // Initialize form data
  useEffect(() => {
    if (lead) {
      setFormData({
        leadType: lead.leadType || 'tour_booking',
        serviceType: lead.serviceType || '',
        assignedTo: lead.assignedTo?._id || lead.assignedTo || '',
        customerName: lead.customerName || lead.name || '',
        contactNumber: lead.contactNumber || lead.phone || '',
        email: lead.email || '',
        tourPackage: lead.tourPackage || '',
        travelDate: lead.travelDate ? new Date(lead.travelDate).toISOString().split('T')[0] : '',
        status: lead.status || 'New',
        address: lead.address || '',
        budget: lead.budget || '',
        destination: lead.destination || '',
        numberOfDays: lead.numberOfDays || ''
      });
    }
  }, [lead]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const leadData = {
        ...formData,
        phone: formData.contactNumber,
        name: formData.customerName
      };

      await onSubmit(leadData);
    } catch (error) {
      console.error('Error submitting lead:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900">
          {lead ? 'Edit Lead' : 'Add New Lead'}
        </h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {/* Lead Type Selection */}
          <div className="sm:col-span-2">
            <label htmlFor="leadType" className="block text-sm font-medium text-gray-700 mb-1">
              🎯 Lead Type *
            </label>
            <select
              name="leadType"
              id="leadType"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.leadType}
              onChange={handleChange}
              required
            >
              <option value="tour_booking">🏖️ Tour Booking</option>
              <option value="other_services">📋 Other Services (Passport, Ticket, Visa, etc.)</option>
            </select>
          </div>

          {/* Service Type - Only show for Other Services */}
          {formData.leadType === 'other_services' && (
            <>
              <div className="sm:col-span-2">
                <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-1">
                  🔧 Service Type *
                </label>
                <select
                  name="serviceType"
                  id="serviceType"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Service Type</option>
                  <option value="passport">🛂 Passport Services</option>
                  <option value="visa">📄 Visa Services</option>
                  <option value="currency">💱 Currency Exchange / Forex</option>
                  <option value="railway_ticketing">🚂 Railway Ticketing</option>
                  <option value="bus_ticketing">🚌 Bus Ticketing</option>
                  <option value="flight_ticketing">✈️ Flight Ticketing</option>
                  <option value="cruise_ticketing">🚢 Cruise Ticketing</option>
                  <option value="car_rental">🚗 Car Rental</option>
                  <option value="insurance">🛡️ Travel Insurance</option>
                  <option value="other">📦 Other Services</option>
                </select>
              </div>

              {/* Assign To Employee - Only show after service type is selected */}
              {formData.serviceType && (
                <div className="sm:col-span-2">
                  <label htmlFor="assignedTo" className="block text-sm font-medium text-gray-700 mb-1">
                    👤 Assign To Employee * 
                    <span className="text-xs text-gray-500 ml-2">
                      (Showing only employees with {formData.serviceType.replace(/_/g, ' ')} specialization)
                    </span>
                  </label>
                  <select
                    name="assignedTo"
                    id="assignedTo"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.assignedTo}
                    onChange={handleChange}
                    required
                  >
                    <option value="">
                      {filteredEmployees.length > 0 
                        ? `Select Employee (${filteredEmployees.length} available)` 
                        : 'No employees available'}
                    </option>
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp) => (
                        <option key={emp._id} value={emp._id}>
                          {emp.teamMemberName || emp.name} - {emp.designation || emp.role}
                          {emp.serviceSpecialization && emp.serviceSpecialization.length > 1 
                            ? ` (${emp.serviceSpecialization.length} services)` 
                            : ''}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No employees available for this service</option>
                    )}
                  </select>
                  {filteredEmployees.length === 0 && formData.serviceType && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        ⚠️ <strong>No employees found</strong> with <strong>{formData.serviceType.replace(/_/g, ' ')}</strong> specialization.
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Please add employees with this service specialization from Employee Management.
                      </p>
                    </div>
                  )}
                  {filteredEmployees.length > 0 && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-xs text-green-700">
                        ✅ {filteredEmployees.length} employee{filteredEmployees.length > 1 ? 's' : ''} available for {formData.serviceType.replace(/_/g, ' ')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Customer Name */}
          <div className="sm:col-span-2">
            <label htmlFor="customerName" className="block text-sm font-medium text-gray-700 mb-1">
              👤 Customer Name *
            </label>
            <input
              type="text"
              name="customerName"
              id="customerName"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.customerName}
              onChange={handleChange}
              required
              placeholder="Enter customer name"
            />
          </div>

          {/* Contact Number */}
          <div>
            <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-1">
              📞 Contact Number *
            </label>
            <input
              type="tel"
              name="contactNumber"
              id="contactNumber"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.contactNumber}
              onChange={handleChange}
              required
              placeholder="Enter contact number"
            />
          </div>

          {/* Email Address */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              ✉️ Email Address *
            </label>
            <input
              type="email"
              name="email"
              id="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="Enter email address"
            />
          </div>

          {/* Tour Package - Only show for Tour Booking */}
          {formData.leadType === 'tour_booking' && (
            <>
              <div className="sm:col-span-2">
                <label htmlFor="tourPackage" className="block text-sm font-medium text-gray-700 mb-1">
                  🎒 Tour Package *
                </label>
                <select
                  name="tourPackage"
                  id="tourPackage"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.tourPackage}
                  onChange={(e) => {
                    const selectedPkg = tourPackages.find(p => p._id === e.target.value);
                    setFormData({
                      ...formData,
                      tourPackage: e.target.value,
                      destination: selectedPkg?.destination || formData.destination,
                      numberOfDays: selectedPkg?.duration?.days || formData.numberOfDays
                    });
                  }}
                  required
                >
                  <option value="">Select Tour Package</option>
                  {tourPackages.map((pkg) => (
                    <option key={pkg._id} value={pkg._id}>
                      {pkg.packageName} - {pkg.destination} ({pkg.duration?.days || 0}D/{pkg.duration?.nights || 0}N)
                    </option>
                  ))}
                </select>
              </div>

              {/* Preferred Travel Date */}
              <div className="sm:col-span-2">
                <label htmlFor="travelDate" className="block text-sm font-medium text-gray-700 mb-1">
                  📅 Preferred Travel Date *
                </label>
                <input
                  type="date"
                  name="travelDate"
                  id="travelDate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={formData.travelDate}
                  onChange={handleChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            </>
          )}

          {/* Notes - For Other Services */}
          {formData.leadType === 'other_services' && (
            <div className="sm:col-span-2">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                📝 Service Requirements
              </label>
              <textarea
                name="notes"
                id="notes"
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={formData.notes || ''}
                onChange={handleChange}
                placeholder="Enter service requirements and details..."
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-6 py-2 rounded-lg transition-colors ${isSubmitting
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
          >
            {isSubmitting
              ? (lead ? 'Updating...' : 'Adding...')
              : (lead ? 'Update Lead' : 'Add Lead')
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadForm;
